/**
 * Утилита для обработки стилей из DOCX файлов
 */

// Стандартные шрифты редактора
const STANDARD_FONTS = [
  'Arial', 'Times New Roman', 'Calibri', 'Georgia', 'Verdana', 
  'Tahoma', 'Helvetica', 'Comic Sans MS', 'Courier New', 'Impact'
];

/**
 * Извлекает и обрабатывает стили из HTML, полученного от mammoth
 * @param {string} html - HTML строка
 * @returns {string} - Обработанный HTML с сохраненными стилями
 */
export function processDocxStyles(html) {
  // Создаем временный DOM элемент для обработки
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Обрабатываем все элементы с атрибутом style
  const styledElements = tempDiv.querySelectorAll('*[style]');
  
  styledElements.forEach(element => {
    const style = element.getAttribute('style') || '';
    const computedStyle = parseInlineStyle(style);
    
    // Обрабатываем шрифты
    if (computedStyle.fontFamily) {
      const font = processFontFamily(computedStyle.fontFamily);
      if (font) {
        element.style.fontFamily = font;
      }
    }
    
    // Обрабатываем размеры шрифта
    if (computedStyle.fontSize) {
      const size = processFontSize(computedStyle.fontSize);
      if (size) {
        element.style.fontSize = size;
      }
    }
    
    // Сохраняем выравнивание
    if (computedStyle.textAlign) {
      element.style.textAlign = computedStyle.textAlign;
    }
    
    // Сохраняем отступы
    if (computedStyle.marginLeft) {
      const padding = convertMarginToPadding(computedStyle.marginLeft);
      element.style.paddingLeft = padding;
    }
    
    // Обрабатываем жирность
    if (computedStyle.fontWeight) {
      element.style.fontWeight = computedStyle.fontWeight;
    }
    
    // Обрабатываем курсив
    if (computedStyle.fontStyle) {
      element.style.fontStyle = computedStyle.fontStyle;
    }
    
    // Обрабатываем подчеркивание
    if (computedStyle.textDecoration) {
      element.style.textDecoration = computedStyle.textDecoration;
    }
  });
  
  // Дополнительная обработка параграфов без явных стилей
  const paragraphs = tempDiv.querySelectorAll('p');
  paragraphs.forEach(p => {
    // Проверяем наличие вложенных span с форматированием
    const spans = p.querySelectorAll('span[style]');
    
    // Если в параграфе есть только один span с одинаковым форматированием
    if (spans.length === 1 && spans[0].textContent.trim() === p.textContent.trim()) {
      const spanStyle = spans[0].getAttribute('style');
      if (spanStyle) {
        // Переносим стили span на параграф
        p.setAttribute('style', (p.getAttribute('style') || '') + '; ' + spanStyle);
        // Заменяем содержимое параграфа на текст span
        p.innerHTML = spans[0].innerHTML;
      }
    }
  });
  
  // Обрабатываем табуляцию
  html = processTabulation(tempDiv.innerHTML);
  
  return html;
}

/**
 * Парсит inline стили в объект
 * @param {string} styleString - Строка стилей
 * @returns {object} - Объект стилей
 */
function parseInlineStyle(styleString) {
  const styles = {};
  if (!styleString) return styles;
  
  styleString.split(';').forEach(rule => {
    const [property, value] = rule.split(':').map(s => s.trim());
    if (property && value) {
      // Конвертируем CSS свойства в camelCase
      const camelProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      styles[camelProperty] = value;
    }
  });
  
  return styles;
}

/**
 * Обрабатывает название шрифта
 * @param {string} fontFamily - Название шрифта
 * @returns {string} - Обработанное название шрифта
 */
function processFontFamily(fontFamily) {
  // Удаляем кавычки
  let cleanFont = fontFamily.replace(/['"]/g, '').trim();
  
  // Проверяем, есть ли шрифт в наших стандартных
  const standardFont = STANDARD_FONTS.find(font => 
    cleanFont.toLowerCase().includes(font.toLowerCase())
  );
  
  if (standardFont) {
    return standardFont;
  }
  
  // Если шрифт не найден, используем Times New Roman по умолчанию
  return 'Times New Roman';
}

/**
 * Обрабатывает размер шрифта
 * @param {string} fontSize - Размер шрифта
 * @returns {string} - Обработанный размер шрифта
 */
function processFontSize(fontSize) {
  // Если размер уже в пикселях, возвращаем как есть
  if (fontSize.endsWith('px')) {
    return fontSize;
  }
  
  // Конвертируем pt в px (1pt = 1.333px примерно)
  if (fontSize.endsWith('pt')) {
    const ptValue = parseFloat(fontSize);
    const pxValue = Math.round(ptValue * 1.333);
    return pxValue + 'px';
  }
  
  // Обрабатываем em (относительно 16px базового размера)
  if (fontSize.endsWith('em')) {
    const emValue = parseFloat(fontSize);
    const pxValue = Math.round(emValue * 16);
    return pxValue + 'px';
  }
  
  // Если число без единиц измерения, считаем что это pt
  const numValue = parseFloat(fontSize);
  if (!isNaN(numValue)) {
    const pxValue = Math.round(numValue * 1.333);
    return pxValue + 'px';
  }
  
  return fontSize;
}

/**
 * Конвертирует margin-left в padding-left
 * @param {string} margin - Значение отступа
 * @returns {string} - Значение padding
 */
function convertMarginToPadding(margin) {
  if (margin.endsWith('pt')) {
    const ptValue = parseFloat(margin);
    const pxValue = Math.round(ptValue * 1.333);
    return pxValue + 'px';
  }
  return margin;
}

/**
 * Обрабатывает табуляцию в тексте
 * @param {string} html - HTML строка
 * @returns {string} - HTML с обработанной табуляцией
 */
function processTabulation(html) {
  // Заменяем последовательности пробелов на табуляцию
  html = html.replace(/(&nbsp;){4,}/g, (match) => {
    const count = match.length / 6; // &nbsp; = 6 символов
    const tabs = Math.floor(count / 4);
    const remainingSpaces = count % 4;
    return '&nbsp;&nbsp;&nbsp;&nbsp;'.repeat(tabs) + '&nbsp;'.repeat(remainingSpaces);
  });
  
  // Обрабатываем множественные пробелы
  html = html.replace(/\s{4,}/g, (match) => {
    const tabs = Math.floor(match.length / 4);
    const remainingSpaces = match.length % 4;
    return '&nbsp;&nbsp;&nbsp;&nbsp;'.repeat(tabs) + '&nbsp;'.repeat(remainingSpaces);
  });
  
  return html;
}

/**
 * Добавляет информацию о масштабировании
 * @param {string} html - HTML строка
 * @returns {string} - HTML с информацией о масштабе
 */
export function addZoomSupport(html) {
  // Оборачиваем контент в div с классом для масштабирования
  return `<div class="docx-content-wrapper" data-zoom="100">${html}</div>`;
}
